import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TreeNodeData } from "../../../services/TreeView/TreeViewAPI.types";
import defaultContent from "../../../content/treeView";
import watchlistContent from "../../../content/watchlistNotifications";
import "./TreeViewNodeModal.scss";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { useAppDispatch, useAppSelector } from "../../../store";
import { IconClose } from "../../../common/icons/IconClose/IconClose";
import { IconArrowLink } from "../../../common/icons/IconArrowLink/IconArrowLink";
import LeafButton, { Variant } from "../../../common/leaf/LeafButton/LeafButton";
import { showProfile as showProfileAction } from "../../../reducers/profile";
import { LOS_GRID_CONFIG, type TreeViewConfig, type ExternalLink } from "../TreeView";
import { IconEye } from "../../../common/icons/IconEye/IconEye";
import { track } from "../../../common/util/tealium";
import { getCurrentPeriod, isValidPeriodFormat } from "../../../common/util/period";
import { storedLocale } from "../../Authentication/util";
import { isChinaAff } from "../../ActionReports/common/ActionReportListView/utils/actionReportUtils";
import { useLocation } from "react-router-dom";
import { trackOpenDownlineProfile } from "../../DownlineProfile/downlineProfileTealium";
import LeafToastMessage from "../../../common/leaf/LeafToastMessage/LeafToastMessage";
import maskAboNumber from "../../../common/util/maskAboNumber";

interface TreeViewNodeModalProps {
  abo: TreeNodeData | null;
  level: number;
  onClose: () => void;
  onExpandGroup: (aboId: string, recursive?: boolean) => void;
  onToggleWatchlist: (abo: TreeNodeData) => Promise<void>;
  isExpanded: boolean;
  canExpand: boolean;
  canIsolate: boolean;
  config: TreeViewConfig;
  externalLinks?: ExternalLink[];
  hideExpandGroup: boolean;
  period?: string;
  isIsolated?: boolean;
  onIsolateChange?: (isolating: boolean) => void;
}

export const TreeViewNodeModal = ({
  abo,
  level,
  onClose,
  onExpandGroup,
  onToggleWatchlist,
  isExpanded,
  canExpand,
  canIsolate,
  config,
  externalLinks = [],
  hideExpandGroup,
  period = "",
  isIsolated = false,
  onIsolateChange,
}: TreeViewNodeModalProps) => {
  const { t } = useTranslation(["treeView", "watchlistNotifications"]);
  const locale = storedLocale();
  const dispatch = useAppDispatch();
  const [isTogglingWatchlist, setIsTogglingWatchlist] = useState(false);
  const [isInWatchlist, setIsInWatchlist] = useState(abo?.watchlist);
  const [toastState, setToastState] = useState<"success" | "error">("success");
  const [showToast, setShowToast] = useState(false);
  const [wasInWatchlist, setWasInWatchlist] = useState(false);
  const [toastCounter, setToastCounter] = useState(0);

  useEffect(() => {
    setIsInWatchlist(abo?.watchlist);
  }, [abo]);

  const { pathname } = useLocation();
  const { profile } = useAppSelector((store) => store.boot.configuration);
  const isEnabled = profile?.enableDownlineProfile ?? false;
  const profileParam = new URLSearchParams(window.location.search).get("profile");
  const isLegacyOverride = profileParam === "legacy";
  const useDownlineProfile = (isEnabled || profileParam === "downline") && !isLegacyOverride;

  const showProfile = config[LOS_GRID_CONFIG.UI_MENU_SHOW_PROFILE] ?? true;
  const showWatchlist = level === 1 ? false : (config[LOS_GRID_CONFIG.UI_MENU_SHOW_WATCHLIST] ?? true);
  const showExpandGroup = !hideExpandGroup;
  const showIsolate =
    canIsolate &&
    (config[LOS_GRID_CONFIG.UI_MENU_SHOW_ISOLATE] ?? false) &&
    (level !== 1 || isIsolated) &&
    !abo?.internationalFlag;
  const isChina = isChinaAff(abo?.aff.toString() || "");

  const isMaskConfidentialAboNumber =
    useAppSelector((s) => s.boot.configuration.los.maskConfidentialAboNumber) ?? false;
  const handleExpandGroup = useCallback(() => {
    if (abo) {
      onExpandGroup(abo.abo.toString(), true);
    }
  }, [abo, onExpandGroup]);

  const handleIsolateChange = useCallback(() => {
    onIsolateChange?.(!isIsolated);
  }, [onIsolateChange, isIsolated]);

  /**
   * Safely open an external link by validating protocol and normalizing the URL.
   * Only http and https protocols are allowed; others are ignored.
   */
  const openSafeExternalLink = (url: string) => {
    try {
      const normalizedUrl = new URL(url, window.location.origin);
      if (normalizedUrl.protocol === "http:" || normalizedUrl.protocol === "https:") {
        window.open(normalizedUrl.toString(), "_blank", "noopener,noreferrer");
      } else {
        // Disallow non-http(s) protocols such as javascript:, data:, etc.
        // eslint-disable-next-line no-console
        console.warn("Blocked opening external link with disallowed protocol:", normalizedUrl.toString());
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Failed to open external link due to invalid URL:", url, e);
    }
  };

  const handleViewProfile = useCallback(() => {
    if (abo) {
      const affAbo = `${abo.aff}-${abo.abo}`;
      dispatch(showProfileAction(affAbo));
      if (useDownlineProfile) {
        trackOpenDownlineProfile(pathname);
      }
      onClose();
    }
  }, [abo, dispatch, onClose, pathname, useDownlineProfile]);

  const handleToggleWatchlist = useCallback(async () => {
    if (abo && !isTogglingWatchlist) {
      setIsTogglingWatchlist(true);
      setWasInWatchlist(isInWatchlist ?? false);
      try {
        await onToggleWatchlist(abo);
        setIsInWatchlist(!isInWatchlist);
        setToastState("success");
      } catch {
        setToastState("error");
      } finally {
        setIsTogglingWatchlist(false);
        setToastCounter((prev) => prev + 1);
        setShowToast(true);
      }
    }
  }, [abo, onToggleWatchlist, isTogglingWatchlist, isInWatchlist]);

  /**
   * Navigate to an external link with interpolated ABO data
   * Replaces {{abo}}, {{aff}}, and {{period}} placeholders in the link URL
   */
  const handleExternalLink = useCallback(
    (link: ExternalLink) => {
      if (!abo) return;

      const safePeriod = isValidPeriodFormat(period) ? period : getCurrentPeriod();
      const interpolatedLink = link.link
        .replace(/\{\{abo\}\}/g, abo.abo.toString())
        .replace(/\{\{aff\}\}/g, abo.aff.toString())
        .replace(/\{\{period\}\}/g, safePeriod);

      openSafeExternalLink(interpolatedLink);
      track("link", "click_action", `navigate link ${abo.abo}-${abo.aff}`, "los", "", "external link");
    },
    [abo, period],
  );

  if (!abo) return null;

  const getDisplayName = (abo: TreeNodeData): string => {
    if (abo.aboAttributes.privacyFlag) {
      return t("confidentialUser", defaultContent.confidentialUser);
    }

    return abo.displayName;
  };

  return (
    <>
      <LeafToastMessage
        showToast={showToast}
        theme={toastState}
        hideProgressBar={true}
        className="mini-notification"
        toastId={`watchlist-notification-${toastCounter}`}
        htmlContent={
          toastState === "success" ? (
            <span>
              {t(
                `watchlistNotifications:notification.${wasInWatchlist ? "removed" : "added"}`,
                wasInWatchlist ? watchlistContent.notification.removed : watchlistContent.notification.added,
                { name: getDisplayName(abo) },
              )}
            </span>
          ) : (
            <span>{t("watchlistNotifications:notification.error", watchlistContent.notification.error)}</span>
          )
        }
      />
      <div className="tree-view-node-modal">
        <div className="tree-view-node-modal__content">
          <section className="tree-view-node-modal__header">
            <div className="tree-view-node-modal__name-container">
              <span className="tree-view-node-modal__name">
                <span className="tree-view-node-modal__level">{level}</span> {getDisplayName(abo)}
              </span>
              <span className="tree-view-node-modal__abo-number">
                #{isMaskConfidentialAboNumber && abo.isConfidential ? maskAboNumber(String(abo.abo)) : abo.abo}
              </span>
            </div>
            <button
              onClick={onClose}
              className="tree-view-node-modal__close-icon"
              aria-label={t("accessibilityLabels.closeModal", defaultContent.accessibilityLabels.closeModal) as string}
            >
              <IconClose width="0.875rem" height="0.875rem" color="black" />
            </button>
          </section>

          <section className="tree-view-node-modal__country">
            <span className="tree-view-node-modal__country--badge">
              {getCountryNameFromCode(abo.aboAttributes.isoCountry, locale)}
            </span>
          </section>

          <section className="tree-view-node-modal__actions" hidden={isChina}>
            {showProfile && !isChina && (
              <LeafButton
                id="tree-view-modal-view-profile"
                clickAction={handleViewProfile}
                variant={Variant.DEFAULT}
                className="tree-view-node-modal__actions--item"
                Icon={<IconArrowLink iconColor="black" weight="thin" />}
              >
                {t("viewProfile", defaultContent.viewProfile)}
              </LeafButton>
            )}

            {showExpandGroup && canExpand && !isChina && (
              <LeafButton
                id="tree-view-modal-expand-group"
                clickAction={handleExpandGroup}
                variant={Variant.DEFAULT}
                className="tree-view-node-modal__actions--item"
                Icon={<IconArrowLink iconColor="black" weight="thin" />}
              >
                {isExpanded
                  ? t("collapseGroup", defaultContent.collapseGroup)
                  : t("expandGroup", defaultContent.expandGroup)}
              </LeafButton>
            )}

            {showWatchlist && !isChina && (
              <LeafButton
                id="tree-view-modal-toggle-watchlist"
                clickAction={() => void handleToggleWatchlist()}
                variant={Variant.DEFAULT}
                className="tree-view-node-modal__actions--item"
                Icon={<IconEye />}
                isDisabled={isTogglingWatchlist}
              >
                {isTogglingWatchlist
                  ? t("updatingWatchlist", defaultContent.updatingWatchlist)
                  : isInWatchlist
                    ? t("removeFromWatchlist", defaultContent.removeFromWatchlist)
                    : t("addToWatchlist", defaultContent.addToWatchlist)}
              </LeafButton>
            )}

            {showIsolate && !isChina && (
              <LeafButton
                id="tree-view-modal-isolate-abo"
                clickAction={handleIsolateChange}
                variant={Variant.DEFAULT}
                className="tree-view-node-modal__actions--item tree-view-node-modal__actions--item-isolate"
                Icon={<IconArrowLink iconColor="blue" weight="thin" />}
              >
                {isIsolated
                  ? t("undoDownlineLos", defaultContent.undoDownlineLos)
                  : t("viewDownlineLos", defaultContent.viewDownlineLos)}
              </LeafButton>
            )}

            {externalLinks.length > 0 && !isChina && (
              <>
                {externalLinks.map((link) => (
                  <LeafButton
                    key={link.name}
                    id={`tree-view-modal-external-${link.name}`}
                    clickAction={() => handleExternalLink(link)}
                    variant={Variant.DEFAULT}
                    className="tree-view-node-modal__actions--item"
                    Icon={<IconArrowLink iconColor="black" weight="thin" />}
                  >
                    {link.label}
                  </LeafButton>
                ))}
              </>
            )}
          </section>
        </div>
      </div>
    </>
  );
};
